<script lang="ts">
  import { untrack } from 'svelte';
  import IconButton from './IconButton.svelte';
  import Icon from './Icon.svelte';
  import { zoom, search, ui, config, isKiosk, layout, displayScheme, goToToday, clearTempMarker, stepFontSize, canResizeUi } from '../lib/state.svelte';
  import { uiSizeGesture } from '../lib/ui-size-gesture';
  import { fitCount, slideWindow } from '../lib/zoom-accordion';
  import { dragStepCount, clampZoomIndex } from '../lib/zoom-drag';
  import { ZOOM_ORDER } from '../lib/types';
  import { online } from '../lib/online.svelte';
  import { viewport } from '../lib/viewport.svelte';
  import { today } from '../lib/today.svelte';
  import { formatDate } from '../lib/format';
  import { createLongPress, loading, countdownBeat, tap, longPress } from '../lib/haptics';
  import {
    primeTimelineAudio,
    suspendTimelineAudio,
    playCountdownTone,
    countdownToneIndex,
  } from '../lib/timeline-music';
  import { clock } from '../lib/clock.svelte';
  import type { Zoom } from '../lib/types';

  type Props = {
    onRefresh: () => Promise<void>;
    onZoom: (z: Zoom, opts?: { jumpToday?: boolean }) => void;
  };
  const { onRefresh, onZoom }: Props = $props();

  const COOLDOWN_MS = 60_000;
  let lastRefresh = $state(0);

  const refreshDisabled = $derived(
    ui.loading || clock.now - lastRefresh < COOLDOWN_MS || !online.value,
  );

  const refreshTitle = $derived(
    !online.value
      ? 'Offline — refresh disabled'
      : ui.loading
        ? 'Loading'
        : refreshDisabled
          ? 'Cooling down'
          : 'Refresh feeds',
  );

  async function handleRefresh(): Promise<void> {
    if (refreshDisabled) return;
    lastRefresh = Date.now();
    loading();
    await onRefresh();
  }

  // Order must match ZOOM_ORDER — the accordion window indexes into both.
  const zooms: { id: Zoom; label: string }[] = [
    { id: 'month', label: '1M' },
    { id: 'quarter', label: '3M' },
    { id: 'half-year', label: '6M' },
    { id: 'year', label: '1Y' },
    { id: '2-year', label: '2Y' },
  ];

  // The 1W button toggles week ↔ last zoom on a single tap; a double tap clears
  // the day marker and returns to today (in week view). Debounce the toggle so a
  // double tap doesn't flip the zoom out and back before the dbl handler runs.
  let weekTapTimer: ReturnType<typeof setTimeout> | null = null;
  function handleWeekClick(): void {
    if (weekTapTimer != null) return; // second tap of a double — let dblclick win
    weekTapTimer = setTimeout(() => {
      weekTapTimer = null;
      onZoom(zoom.value === 'week' ? zoom.lastNonWeek : 'week');
    }, 230);
  }
  function handleWeekDblClick(): void {
    if (weekTapTimer != null) {
      clearTimeout(weekTapTimer);
      weekTapTimer = null;
    }
    goToToday({ zoom: 'week', clearMarker: true });
  }

  // Easter egg: hold the date button through a three-beat countdown — "ding,
  // dung, dong" at 1s/2s/3s — and on the third beat the timeline starts playing
  // as music automatically (hold again to stop). Enabling ascends the chime;
  // disabling plays it in reverse. Once on, the date icon becomes a bell. Audio
  // is primed on pointerdown so the countdown beeps are audible within the user
  // gesture (Firefox and iOS Safari require this); an unused quick tap releases
  // the context again so jump-to-today leaves no audio on.
  const HOLD_STEP_MS = 1000;
  const HOLD_STEPS = 3;
  let holdTimers: ReturnType<typeof setTimeout>[] = [];
  let holdActivated = false;
  let suppressTitleClick = false;
  // With a temp marker set, the date button doubles as the today↔marker toggle
  // (replacing the in-view cycle button), so it shows the cycle glyph.
  const titleIcon = $derived(
    ui.timelineMusic ? 'bell' : ui.tempMarkerMs != null ? 'arrows-horizontal' : 'app-today',
  );
  const titleLabel = $derived(
    ui.tempMarkerMs != null
      ? 'Toggle between today and marker (double-click to clear)'
      : 'Jump to today',
  );

  function clearHoldTimers(): void {
    for (const t of holdTimers) clearTimeout(t);
    holdTimers = [];
  }

  // Which hold a first finger is in, so a pinch (cancelPresses) ends only that
  // one and swallows only its click.
  let pressing: 'title' | 'search' | 'settings' | null = null;

  function startTitlePress(e: PointerEvent): void {
    if (!e.isPrimary) return;
    pressing = 'title';
    suppressTitleClick = false;
    holdActivated = false;
    // Direction is fixed at press start: ascending to enable, reversed to
    // disable. Only this hold flips the flag, so the captured value stays valid.
    const enabling = !ui.timelineMusic;
    primeTimelineAudio();
    for (let beat = 1; beat <= HOLD_STEPS; beat++) {
      holdTimers.push(
        setTimeout(() => {
          playCountdownTone(countdownToneIndex(beat, enabling, HOLD_STEPS));
          countdownBeat();
          if (beat === HOLD_STEPS) {
            holdActivated = true;
            suppressTitleClick = true;
            ui.timelineMusic = enabling;
          }
        }, beat * HOLD_STEP_MS),
      );
    }
  }

  function endTitlePress(): void {
    if (pressing === 'title') pressing = null;
    clearHoldTimers();
    if (!holdActivated && !ui.timelineMusic) suspendTimelineAudio();
  }

  function handleTitleClick(): void {
    if (suppressTitleClick) {
      suppressTitleClick = false;
      return;
    }
    // With a marker set, the button cycles today↔marker (handled by whichever
    // view is mounted); otherwise it jumps to today.
    if (ui.tempMarkerMs != null) {
      window.dispatchEvent(new CustomEvent('cal:toggle-marker'));
    } else {
      goToToday();
    }
  }

  function toggleSearch(): void {
    search.open = !search.open;
    if (search.open) {
      queueMicrotask(() => {
        document.querySelector<HTMLInputElement>('input[data-search-input]')?.focus();
      });
    } else {
      // Leaving search mode drops the query too, so the match-highlight /
      // hide-non-matching treatment doesn't linger on the timeline.
      search.query = '';
    }
  }

  // Long-press the search button to reveal the keyboard-shortcuts modal;
  // suppressSearchClick swallows the click that trails the hold so it doesn't
  // also toggle search (mirrors the settings button's suppressClick).
  const shortcutsPress = createLongPress();
  let suppressSearchClick = false;

  function startSearchPress(e: PointerEvent): void {
    if (!e.isPrimary) return;
    pressing = 'search';
    suppressSearchClick = false;
    shortcutsPress.start(() => {
      suppressSearchClick = true;
      ui.shortcutsOpen = true;
    });
  }

  function endSearchPress(): void {
    if (pressing === 'search') pressing = null;
    shortcutsPress.cancel();
  }

  function handleSearchClick(): void {
    if (suppressSearchClick) {
      suppressSearchClick = false;
      return;
    }
    toggleSearch();
  }

  // In cycle mode the button reflects the current position: the marker's date
  // when centered on it, otherwise today's.
  const displayDate = $derived(
    ui.tempMarkerMs != null && ui.markerFocus === 'marker'
      ? new Date(ui.tempMarkerMs)
      : today.value,
  );
  const dateLabel = $derived(formatDate(displayDate, config.dateFormat, config.locale));

  // One hold, two outcomes: released between 500ms and 3s flips the theme (on
  // release, not mid-hold); holding to 3s locks/unlocks kiosk instead.
  const THEME_PRESS_MS = 500;
  const kioskPress = createLongPress(3000);
  let pressStart = 0;
  // Fires a haptic the moment the hold crosses the theme-flip threshold, so the
  // user feels that releasing now will flip the scheme (the flip itself still
  // happens on release, in endSettingsPress).
  let themeArmTimer: ReturnType<typeof setTimeout> | null = null;
  let suppressClick = false;
  let tempIcon = $state<string | null>(null);
  let iconTimer: ReturnType<typeof setTimeout> | null = null;
  const settingsIcon = $derived(tempIcon ?? (isKiosk() ? 'lock' : 'settings'));
  const settingsLabel = $derived(
    isKiosk()
      ? 'Kiosk locked (long-press 3s to unlock)'
      : 'Settings (long-press: flip scheme; hold 3s to lock)',
  );

  function flipTheme(target: HTMLElement | null): void {
    // Flip away from what's on screen, which for the 'auto' and 'shake' modes is
    // the resolved value rather than the stored one. Pinning light/dark here is
    // an explicit choice, so it deliberately leaves Shake mode.
    const effective = displayScheme(viewport.prefersDark);
    config.scheme = effective === 'dark' ? 'light' : 'dark';
    target?.blur();
    tempIcon = config.scheme === 'dark' ? 'moon' : 'sun';
    if (iconTimer) clearTimeout(iconTimer);
    iconTimer = setTimeout(() => { tempIcon = null; }, 2000);
  }

  function startSettingsPress(e: PointerEvent): void {
    if (!e.isPrimary) return;
    pressing = 'settings';
    pressStart = Date.now();
    suppressClick = false;
    if (themeArmTimer) clearTimeout(themeArmTimer);
    themeArmTimer = setTimeout(() => {
      themeArmTimer = null;
      longPress();
    }, THEME_PRESS_MS);
    kioskPress.start(() => {
      suppressClick = true;
      ui.kioskPinModal = isKiosk() ? 'unlock' : 'set';
    });
  }

  function endSettingsPress(e: PointerEvent): void {
    // Ended already (a pinch took over): a late release must not flip the theme.
    if (pressing !== 'settings') return;
    pressing = null;
    const elapsed = Date.now() - pressStart;
    if (themeArmTimer) {
      clearTimeout(themeArmTimer);
      themeArmTimer = null;
    }
    const kioskFired = kioskPress.didFire();
    kioskPress.cancel();
    // Held to the lock threshold: kiosk modal already handled it — never flip.
    if (kioskFired) return;
    // Released after a deliberate hold but before the lock: flip the theme now.
    if (elapsed >= THEME_PRESS_MS) {
      flipTheme(e.currentTarget as HTMLElement);
      suppressClick = true;
    }
  }

  function abortSettingsPress(): void {
    if (pressing === 'settings') pressing = null;
    if (themeArmTimer) {
      clearTimeout(themeArmTimer);
      themeArmTimer = null;
    }
    kioskPress.cancel();
  }

  function handleSettingsClick(): void {
    if (suppressClick) {
      suppressClick = false;
      return;
    }
    if (isKiosk()) return;
    ui.settingsOpen = !ui.settingsOpen;
  }

  let rightGroupEl: HTMLElement | undefined = $state();
  let zoomNavEl: HTMLElement | undefined = $state();
  let toolbarEl: HTMLElement | undefined = $state();
  let spacerEl: HTMLElement | undefined = $state();
  let weekBtnEl: HTMLButtonElement | undefined = $state();

  // Accordion zoom nav: when the toolbar can't fit all zoom buttons, the ones
  // at the edges collapse into thin clickable slivers; the expanded buttons
  // form a contiguous window that always contains the active zoom (window math
  // in zoom-accordion.ts). Collapsed buttons stay in the DOM so widths animate
  // and measurement stays simple.
  const SLIVER_W = 8; // px; keep in sync with --zoom-sliver-w in the styles below
  let visibleCount = $state(ZOOM_ORDER.length);
  let windowStart = $state(0);
  // Cached widest expanded button, so the fit test still knows the full width
  // while buttons are collapsed (or mid-transition).
  let buttonW = 0;

  const activeIndex = $derived(
    ZOOM_ORDER.indexOf(zoom.value === 'week' ? zoom.lastNonWeek : zoom.value),
  );
  // On a portrait phone 2Y can't show its two years across the screen (it
  // scrolls, see FIT_WHOLE_SPAN_MIN_WIDTH), so its button stays a sliver there
  // as a hint, unless it's the active zoom: the window only spans the rest.
  const windowTotal = $derived(
    viewport.isPortraitMobile && activeIndex !== ZOOM_ORDER.length - 1
      ? ZOOM_ORDER.length - 1
      : ZOOM_ORDER.length,
  );
  const windowCount = $derived(Math.min(visibleCount, windowTotal));
  $effect(() => {
    windowStart = slideWindow(
      untrack(() => windowStart),
      activeIndex,
      windowCount,
      windowTotal,
    );
  });
  const isCollapsed = (i: number): boolean => i < windowStart || i >= windowStart + windowCount;

  // Touch-drag across the zoom nav to scrub through the zoom levels (drag right
  // → longer ranges, left → shorter), one step per DRAG_STEP_PX of travel.
  // Touch/pen only — mouse has the wheel + click; a drag also suppresses the
  // release-tap so it doesn't double-fire on the button under the finger.
  const DRAG_STEP_PX = 40; // ~ one zoom button's width
  const DRAG_THRESHOLD_PX = 8; // travel before a press becomes a drag (vs a tap)
  let dragPointerId: number | null = null;
  let dragStartX = 0;
  let dragAnchorX = 0;
  let dragMoved = false;
  let suppressZoomClick = false;

  function stepZoom(steps: number): void {
    const current = zoom.value === 'week' ? zoom.lastNonWeek : zoom.value;
    const i = ZOOM_ORDER.indexOf(current);
    if (i < 0) return;
    const target = clampZoomIndex(i, steps, ZOOM_ORDER.length);
    if (target === i) return;
    onZoom(ZOOM_ORDER[target]!);
    tap();
    if (ui.musicSweeping) goToToday();
  }

  function onNavPointerDown(e: PointerEvent): void {
    if (e.pointerType === 'mouse' || !e.isPrimary) return;
    dragPointerId = e.pointerId;
    dragStartX = e.clientX;
    dragAnchorX = e.clientX;
    dragMoved = false;
    // A fresh press clears any suppression left over from a drag that ended off
    // a button (no click came to consume it), so this tap still registers.
    suppressZoomClick = false;
  }

  function onNavPointerMove(e: PointerEvent): void {
    if (e.pointerId !== dragPointerId) return;
    if (!dragMoved && Math.abs(e.clientX - dragStartX) < DRAG_THRESHOLD_PX) return;
    if (!dragMoved) {
      dragMoved = true;
      zoomNavEl?.setPointerCapture(dragPointerId);
    }
    // Always advance the anchor by the whole steps traversed — even when the
    // zoom is already clamped at an end — so reversing the drag responds at once.
    const steps = dragStepCount(e.clientX - dragAnchorX, DRAG_STEP_PX);
    if (steps !== 0) {
      dragAnchorX += steps * DRAG_STEP_PX;
      stepZoom(steps);
    }
  }

  function onNavPointerUp(e: PointerEvent): void {
    if (e.pointerId !== dragPointerId) return;
    // Swallow the click the browser fires after a drag-release on a button.
    if (dragMoved) suppressZoomClick = true;
    dragPointerId = null;
    dragMoved = false;
  }

  // A second finger makes it a UI-size pinch (second fingers start nothing:
  // each press handler bails on !isPrimary). End whatever the first finger
  // began — zoom scrub, title countdown, search or settings hold — and swallow
  // the click its release might fire. A pinch usually fires no click, so
  // endPinch drops the suppression once the click's moment has passed, or a
  // later mouse click or Enter would be eaten.
  function cancelPresses(): void {
    if (dragPointerId != null) {
      if (zoomNavEl?.hasPointerCapture(dragPointerId)) zoomNavEl.releasePointerCapture(dragPointerId);
      dragPointerId = null;
      dragMoved = false;
      suppressZoomClick = true;
    }
    if (pressing === 'title') {
      endTitlePress();
      suppressTitleClick = true;
    } else if (pressing === 'search') {
      endSearchPress();
      suppressSearchClick = true;
    } else if (pressing === 'settings') {
      abortSettingsPress();
      suppressClick = true;
    }
  }

  // A click that trails a touch release lands within a frame or two of it.
  const PINCH_CLICK_GRACE_MS = 100;
  function endPinch(): void {
    setTimeout(() => {
      suppressZoomClick = false;
      suppressTitleClick = false;
      suppressSearchClick = false;
      suppressClick = false;
    }, PINCH_CLICK_GRACE_MS);
  }

  function onZoomButtonClick(id: Zoom): void {
    if (suppressZoomClick) {
      suppressZoomClick = false;
      return;
    }
    onZoom(id);
    if (ui.musicSweeping) goToToday();
  }

  $effect(() => {
    if (typeof document === 'undefined') return;
    // All reads first, then the writes, and nothing written to :root: a custom
    // property set there invalidates style for the whole document, so the next
    // layout read (here or in the next effect) forced a full style + layout pass
    // over every timeline row — hundreds of ms at startup on a busy timeline.
    const update = (): void => {
      // Right edge of the 6M button (viewport x; the header starts at x=0) — the
      // search field stretches its right edge to match (SearchToolbar), and the
      // timeline anchors on it (layout.zoomNavRight) until the 1W–1M gap is
      // measured. When 6M is collapsed, fall back to the
      // rightmost expanded zoom button; with every one collapsed there's no line
      // worth aligning to, so the timeline falls back to centring (0).
      const expanded = zoomNavEl
        ? Array.from(zoomNavEl.querySelectorAll<HTMLElement>('[data-zoom]:not([data-collapsed])'))
        : [];
      const sixM = zoomNavEl?.querySelector<HTMLElement>('[data-zoom="half-year"]:not([data-collapsed])');
      const searchAnchor = sixM ?? expanded[expanded.length - 1];
      const right = searchAnchor ? Math.round(searchAnchor.getBoundingClientRect().right) : 0;
      // How many zoom buttons fit. The budget — nav + spacer minus any overflow
      // of the row — is what the nav may occupy, and it is invariant to how many
      // buttons are collapsed (collapsing moves width from nav to spacer 1:1),
      // so the count can't oscillate on its own resizes. 2px slack absorbs
      // offsetWidth rounding.
      let budget: number | null = null;
      if (toolbarEl && spacerEl && zoomNavEl) {
        for (const btn of expanded) buttonW = Math.max(buttonW, btn.offsetWidth);
        const overhang = Math.max(0, toolbarEl.scrollWidth - toolbarEl.clientWidth);
        budget = zoomNavEl.offsetWidth + spacerEl.offsetWidth - overhang - 2;
      }

      if (layout.zoomNavRight !== right) layout.zoomNavRight = right;
      if (budget !== null && buttonW > 0) {
        visibleCount = fitCount(budget, buttonW, SLIVER_W, ZOOM_ORDER.length);
      }
    };
    // untrack: the sync call reads visibleCount-dependent DOM / element sizes —
    // the effect should only re-run when the bound element refs change, not on
    // those reads.
    untrack(update);
    // Re-learn the cached button width once the row comes to rest: the cache
    // only grows during a resize storm (mid-transition measurements would
    // poison it low), but the 640px media query genuinely shrinks the buttons,
    // so a trailing full re-measure keeps it from sticking high after that.
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    const onResize = (): void => {
      update();
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        buttonW = 0;
        update();
      }, 220);
    };
    const ro = new ResizeObserver(onResize);
    if (rightGroupEl) ro.observe(rightGroupEl);
    if (zoomNavEl) ro.observe(zoomNavEl);
    if (toolbarEl) ro.observe(toolbarEl);
    return () => {
      ro.disconnect();
      if (settleTimer) clearTimeout(settleTimer);
    };
  });

  // Publish the 1W button's left-edge x so the week grid can line its frozen
  // timezone gutter's right border up with it, and the middle of the gap between
  // 1W and the first zoom button (1M) — where the timeline parks today.
  // Re-measures when the date label's width changes (it shifts the buttons) and
  // on any toolbar or zoom-nav resize (spacing changes the gap); the button's
  // own box doesn't resize, so the dateLabel dependency drives re-measurement.
  $effect(() => {
    if (typeof ResizeObserver === 'undefined' || !weekBtnEl) return;
    dateLabel; // re-measure when the title width (hence the button) shifts
    const measure = (): void => {
      if (!weekBtnEl) return;
      const week = weekBtnEl.getBoundingClientRect();
      const left = Math.round(week.left);
      if (layout.weekBtnLeft !== left) layout.weekBtnLeft = left;
      const next = zoomNavEl?.querySelector<HTMLElement>('[data-zoom]');
      const mid = next ? Math.round((week.right + next.getBoundingClientRect().left) / 2) : 0;
      if (layout.weekGapMid !== mid) layout.weekGapMid = mid;
    };
    // untrack: measure() compares against the layout values it writes, which
    // would otherwise make this effect re-run (and rebuild its observer) on
    // every change it publishes.
    untrack(measure);
    const ro = new ResizeObserver(measure);
    ro.observe(weekBtnEl);
    if (toolbarEl) ro.observe(toolbarEl);
    if (zoomNavEl) ro.observe(zoomNavEl);
    return () => ro.disconnect();
  });
</script>

<header
  class="toolbar"
  bind:this={toolbarEl}
  use:uiSizeGesture={{ onStep: stepFontSize, enabled: canResizeUi, onPinchStart: cancelPresses, onPinchEnd: endPinch }}
>
  <button
    class="title"
    type="button"
    data-marker={ui.tempMarkerMs != null ? 'true' : null}
    onclick={handleTitleClick}
    ondblclick={clearTempMarker}
    onpointerdown={startTitlePress}
    onpointerup={endTitlePress}
    onpointercancel={endTitlePress}
    onpointerleave={endTitlePress}
    aria-label={titleLabel}
    title={titleLabel}
  >
    <Icon name={titleIcon} size={23} />
    <time datetime={displayDate.toISOString().slice(0, 10)}>{dateLabel}</time>
  </button>
  <button
    class="zoom-btn week-btn"
    type="button"
    bind:this={weekBtnEl}
    aria-pressed={zoom.value === 'week'}
    title="1W · week view · double-tap to clear marker and jump to today"
    onclick={handleWeekClick}
    ondblclick={handleWeekDblClick}
  >1W</button>
  <nav
    aria-label="Zoom"
    bind:this={zoomNavEl}
    onpointerdown={onNavPointerDown}
    onpointermove={onNavPointerMove}
    onpointerup={onNavPointerUp}
    onpointercancel={onNavPointerUp}
  >
    <!-- Buttons outside the accordion window collapse into thin slivers (still
         clickable — tapping one switches to that zoom, which expands it).
         Dragging across the nav scrubs through the zoom levels (see stepZoom). -->
    {#each zooms as z, i (z.id)}
      <button
        class="zoom-btn"
        type="button"
        data-zoom={z.id}
        data-collapsed={isCollapsed(i) ? 'true' : null}
        aria-pressed={zoom.value === z.id}
        title="{z.label} · drag to change zoom · double-tap to jump to today"
        onclick={() => onZoomButtonClick(z.id)}
        ondblclick={() => goToToday({ zoom: z.id, clearMarker: true })}
      ><span class="zoom-label">{z.label}</span></button>
    {/each}
  </nav>
  <span class="spacer" bind:this={spacerEl}></span>
  <span class="toolbar-right" bind:this={rightGroupEl}>
    {#if !isKiosk()}
      <span class="refresh-wrap" data-spinning={ui.loading ? 'true' : null}>
        <IconButton
          icon="refresh"
          label={refreshTitle}
          title={refreshTitle}
          disabled={refreshDisabled}
          onclick={() => void handleRefresh()}
        />
      </span>
    {/if}
    <span
      class="settings-wrap"
      role="presentation"
      onpointerdown={startSettingsPress}
      onpointerup={endSettingsPress}
      onpointercancel={abortSettingsPress}
      onpointerleave={abortSettingsPress}
    >
      <IconButton
        icon={settingsIcon}
        label={settingsLabel}
        title={settingsLabel}
        pressed={ui.settingsOpen}
        onclick={handleSettingsClick}
      />
    </span>
    {#if !isKiosk()}
      <span
        class="search-wrap"
        role="presentation"
        onpointerdown={startSearchPress}
        onpointerup={endSearchPress}
        onpointercancel={endSearchPress}
        onpointerleave={endSearchPress}
      >
        <IconButton
          icon="search"
          label="Search events (long-press for keyboard shortcuts)"
          title="Search events · long-press for keyboard shortcuts"
          pressed={search.open}
          onclick={handleSearchClick}
        />
      </span>
    {/if}
  </span>
</header>

<style>
  .toolbar {
    /* Pinch is ours (UI size, ui-size-gesture.ts), never the page's zoom. */
    touch-action: pan-x pan-y;
    display: flex;
    align-items: center;
    gap: var(--toolbar-gap);
    padding: var(--time-header-pad-x);
    height: var(--toolbar-h);
    border-bottom: var(--border-w) solid var(--ink-color);
    background: var(--paper-color);
    position: sticky;
    top: 0;
    z-index: var(--z-chrome);
    /* Shift right with the timeline when the desktop left tray is open (0 when
       closed), so the controls sit to the right of the tray. The .spacer keeps
       the right-side icons pinned to the viewport edge. */
    margin-left: var(--tray-left-w, 0);
    transition: margin-left var(--dur-ui) ease;
  }
  .title {
    display: inline-flex;
    align-items: center;
    gap: 0.5em;
    height: 32px;
    padding: 0 0.6em;
    border: var(--btn-border-w) solid var(--ink-color);
    background: var(--paper-color);
    color: var(--ink-color);
    cursor: pointer;
    flex-shrink: 0;
  }
  /* In cycle mode (a temp marker is set) the date button reads in accent — the
     Icon uses currentColor, so the glyph tints along with the text and border. */
  .title[data-marker='true'] {
    color: var(--accent-color);
    border-color: var(--accent-color);
  }
  /* The date (mono) and the zoom labels (sans) trim their line boxes to the
     digits' cap height, so each centres on its button's midline — the icon's —
     whatever the platform fonts' ascent/descent would otherwise shift. */
  .title time,
  .zoom-btn {
    text-box: trim-both cap alphabetic;
  }
  .title time {
    font-size: var(--fs-13);
    white-space: nowrap;
  }
  nav {
    display: inline-flex;
    gap: 0;
    flex-shrink: 0;
    /* Width of a collapsed (sliver) zoom button; keep in sync with SLIVER_W. */
    --zoom-sliver-w: 8px;
    /* Claim horizontal drags for zoom scrubbing; leave vertical page scroll. */
    touch-action: pan-y;
  }
  .zoom-btn {
    height: 32px;
    border: var(--btn-border-w) solid var(--ink-color);
    border-radius: 0;
    background: var(--paper-color);
    color: var(--ink-color);
    cursor: pointer;
    font-size: var(--fs-12);
    /* Square (matches the 32px control height). Collapsed slivers animate via the
       [data-collapsed] min/max-width override below. */
    min-width: 32px;
    max-width: 32px;
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    transition: min-width var(--dur-ui) ease, max-width var(--dur-ui) ease, padding var(--dur-ui) ease;
  }
  .zoom-btn[data-collapsed] {
    min-width: var(--zoom-sliver-w);
    max-width: var(--zoom-sliver-w);
    padding: 0;
  }
  .zoom-label {
    transition: opacity var(--dur-ui) ease;
  }
  .zoom-btn[data-collapsed] .zoom-label {
    opacity: 0;
  }
  .zoom-btn + .zoom-btn {
    border-left-width: 0;
  }
  .zoom-btn:first-of-type {
    border-top-left-radius: var(--btn-radius);
    border-bottom-left-radius: var(--btn-radius);
  }
  .zoom-btn:last-of-type {
    border-top-right-radius: var(--btn-radius);
    border-bottom-right-radius: var(--btn-radius);
  }
  .zoom-btn[aria-pressed='true'] {
    background: var(--ink-color);
    color: var(--paper-color);
  }
  /* The week toggle stands apart from the zoom progression: its own rounded
     button with a small gap before the 1M–1Y group. */
  .week-btn {
    margin-right: var(--toolbar-gap);
    border-radius: var(--btn-radius);
    flex-shrink: 0;
  }
  .spacer {
    flex: 1;
  }
  .toolbar-right {
    display: inline-flex;
    align-items: center;
    gap: var(--toolbar-gap);
    flex-shrink: 0;
  }
  .refresh-wrap,
  .settings-wrap,
  .search-wrap {
    display: inline-flex;
  }
  .refresh-wrap[data-spinning='true'] :global(.icon-button) :global(svg),
  .refresh-wrap[data-spinning='true'] :global(.icon-button) :global(.icon) {
    animation: cal-spin 800ms linear infinite;
    transform-origin: 50% 50%;
  }
  @keyframes cal-spin {
    from { transform: rotate(0); }
    to { transform: rotate(360deg); }
  }
  @media (max-width: 640px) {
    /* No date label on phones → the current-day button collapses to a square
       icon button like the rest (on desktop it keeps expanding with the date). */
    .title {
      width: 32px;
      padding: 0;
      justify-content: center;
    }
    .title time {
      display: none;
    }
    .zoom-btn {
      font-size: var(--fs-11);
    }
  }
</style>
